import {Helmet} from '@dr.pogodin/react-helmet';
import {Wizard} from 'platform/components';

import {useEffect, useMemo} from 'react';
import {useWatch} from 'react-hook-form';

import {useCreateVehicleMutation, usePutRentalVehicleMutation} from '@dms/api/vehicle';
import {
  RentalVehicle,
  RentalVehicleValidationSchema,
  toAPIVehicle,
  useAdditionalNote,
  useFormKeyboardNavigation,
  useGetCarFeatures,
  useVehicleDocumentSync,
  useVehicleFormContext,
  useWizardTabRouting,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {formatDocumentTitle, FullScreenModal, handleApiError} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {RentalBasicInformation} from '../(sections)/RentalBasicInformation';
import {RentalFeatures} from '../(sections)/RentalFeatures';
import {RentalInformation} from '../(sections)/RentalInformation';
import {RentalVehicleData} from '../(sections)/RentalVehicleData';
import {ServiceIntervals} from '../(sections)/ServiceIntervals';
import {CREATE_RENTAL_VEHICLE_DEFAULT_VALUES} from '../constants/CREATE_RENTAL_VEHICLE_DEFAULT_VALUES';
import {useRentalVehicleWizard} from '../hooks/useRentalVehicleWizard';

interface CreateRentalVehicleFormProps {
  isCreating: boolean;
  onSave: (values: RentalVehicle) => void;
  onDiscard: () => void;
  onVehicleTypeChange?: (type: string) => void;
}

const CreateRentalVehicleForm = ({
  onSave,
  onDiscard,
  isCreating,
  onVehicleTypeChange,
}: CreateRentalVehicleFormProps) => {
  const {control, formApi} = useVehicleFormContext<RentalVehicle>();

  const wizardSteps = useRentalVehicleWizard(control, {
    basicInformation: <RentalBasicInformation />,
    vehicleData: <RentalVehicleData />,
    features: <RentalFeatures />,
    serviceIntervals: <ServiceIntervals />,
    rentalInformation: <RentalInformation />,
  });

  const tabStepMap = useMemo(
    () => ({
      'basic-information': 0,
      'vehicle-data': 1,
      features: 2,
      'service-intervals': 3,
      'rental-information': 4,
    }),
    []
  );

  const {wizardRef, onStepChange} = useWizardTabRouting({
    tabStepMap,
    tabRoutePattern: vehiclesRoutes.createRentalVehicleV2,
    defaultTab: 'basic-information',
  });

  const {onKeyDownCapture} = useFormKeyboardNavigation({
    wizardRef,
    onStepChange,
  });

  const vehicleTypeWatch = useWatch({
    control,
    name: 'type',
  });

  useEffect(() => {
    onVehicleTypeChange?.(vehicleTypeWatch?.[0] as string);
  }, [vehicleTypeWatch]);

  return (
    <FullScreenModal
      data-testid="createRentalVehicle"
      headline={i18n.t('entity.vehicle.labels.createRentalVehicle')}
      backgroundColor="general.white"
      scrollable={false}
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isCreating,
          onClick: onDiscard,
        },
        {
          title: i18n.t('general.actions.create'),
          isLoading: isCreating,
          onClick: () => {
            formApi.handleSubmit(onSave, (error) => {
              if (process.env.ENV_TYPE === 'dev') {
                console.error(error);
              }
            })();
          },
        },
      ]}
    >
      <Wizard
        ref={wizardRef}
        orientation="horizontal"
        steps={wizardSteps}
        height="100%"
        onStepChange={onStepChange}
        onKeyDownCapture={onKeyDownCapture}
      />
    </FullScreenModal>
  );
};

export function CreateRentalVehicle() {
  const navigate = useNavigate();

  const [getCarFeatures] = useGetCarFeatures();
  const [createRentalVehicle] = usePutRentalVehicleMutation();
  const [createVehicleMutation, {isLoading: isCreating}] = useCreateVehicleMutation();

  const {handleSaveNote} = useAdditionalNote();
  const {syncDocuments} = useVehicleDocumentSync();

  const handleSave = async (values: RentalVehicle) => {
    const {rentalProperties, ...vehicleSubmitValues} = values;

    const sourcingData = await getCarFeatures(values);
    const apiPayload = toAPIVehicle(vehicleSubmitValues, null, sourcingData);
    const onCreate = () =>
      createVehicleMutation({
        createVehicleRequestBody: apiPayload,
      })
        .unwrap()
        .then((createdVehicle) => createdVehicle.id)
        .then(async (id) => {
          await createRentalVehicle({
            vehicleId: id,
            rentalVehicleRequestBody: rentalProperties,
          }).unwrap();

          return id;
        })
        // Handle additional note
        .then(async (id) => {
          if (vehicleSubmitValues.additionalNote) {
            await handleSaveNote(id, vehicleSubmitValues.additionalNote);
          }
          return id;
        })
        // Sync battery certificate document
        .then(async (id) => {
          await syncDocuments({
            vehicleId: id,
            contextTarget: 'vehicle',
            fileFields: [
              {
                fieldValue: vehicleSubmitValues.batteryCertificateDocument,
                documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
              },
            ],
          });
          return id;
        })
        .then((id) => {
          navigate(vehiclesRoutes.detail.replace(':id', id));
        });

    // eslint-disable-next-line no-restricted-syntax
    return onCreate().catch(handleApiError);
  };

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.createRentalVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>

      <VFKit.Form
        mode="onBlur"
        experimentalZodSchema={RentalVehicleValidationSchema}
        defaultValues={CREATE_RENTAL_VEHICLE_DEFAULT_VALUES}
      >
        <CreateRentalVehicleForm
          onSave={handleSave}
          onDiscard={() => navigate(-1)}
          isCreating={isCreating}
        />
      </VFKit.Form>
    </>
  );
}
